/**
 * ShellRouter — Generates React Router routes from RouteModule config.
 * Each route is wrapped in React.lazy() + Suspense for code splitting.
 * @layer layout
 * @package @plerity/dashboard-shell
 */

import { PageSkeleton } from "@plerity/dashboard-shared/components";
import { ErrorBoundary } from "@plerity/ui-core/ErrorBoundary";
import type React from "react";
import { lazy, type ReactNode, Suspense } from "react";
import { Navigate, Route } from "react-router-dom";
import type { RouteModule } from "./types";

/** Wrapper that provides error boundary + suspense for lazy routes */
function LazyRoute({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

/** Create a lazy component from a RouteModule loader */
function createLazyComponent(loader: NonNullable<RouteModule["loader"]>) {
  return lazy(loader);
}

/**
 * Recursively build Route elements from RouteModule config.
 *
 * IMPORTANT: `RouteModule.children` are used for two purposes:
 *   1. Sidebar navigation sub-menus (expandable groups in SidebarNav).
 *   2. Routing — each child's `path` MUST be a full absolute path.
 *
 * Apps in this codebase (CRM, Finance, AI, SEO, Design, Outreach, Ops, Email)
 * configure `children` as **full absolute paths** like `"crm/leads"` because
 * each expandable sub-menu item must be navigable at its own full URL.
 *
 * If we render children as nested React Router routes, React Router treats the
 * child path as **relative** to the parent — producing broken URLs such as
 * `/crm/leads/crm/opportunities` (2026-04-09 regression from commit 389083ce35).
 *
 * The correct behavior for this codebase is to emit each route — parent AND
 * children — as **flat sibling Routes**. Duplicates (parent/first-child sharing
 * the same path) are deduped by path so each URL maps to one element.
 *
 * RouteModule.index is treated as **sidebar metadata only** in the flat-sibling
 * model — it signals "this route is the landing page for its group" to nav
 * rendering but has no routing effect. Previously `index: true` caused us to
 * emit `<Route index />` with no path, which broke any index-marked parent
 * (e.g. `/finance/individual-tax`) because `<Route index>` inside the layout
 * wrapper only matches the shell root, not the parent's path.
 *
 * Relative child paths (e.g. `":id"`) are NOT supported in the flat-sibling
 * model — they would emit a top-level `<Route path=":id">` that matches any
 * single-segment URL. If you need URLs like `/inbox/:threadId`, configure the
 * child as `{ path: "inbox/:threadId" }` (absolute), not `{ path: ":threadId" }`.
 */
function buildRouteElements(route: RouteModule, seen: Set<string>): React.JSX.Element[] {
  const elements: React.JSX.Element[] = [];

  // External routes (external: true, no loader) are sidebar-only deep-links
  // to another shell — they do NOT register a React Router <Route>.
  if (route.external || !route.loader) {
    return elements;
  }

  const loader = route.loader;
  const LazyComponent = createLazyComponent(loader);

  // Dedupe by path — sibling or parent/child pairs that share a path collapse to
  // a single Route (first loader wins, matching previous implicit behavior).
  // `route.index` is intentionally NOT part of the dedup key — it's sidebar
  // metadata only in the flat-sibling model.
  if (!seen.has(route.path)) {
    seen.add(route.path);
    elements.push(
      <Route
        key={route.path}
        path={route.path}
        element={
          <LazyRoute>
            <LazyComponent />
          </LazyRoute>
        }
      />
    );
  }

  // Emit children as flat siblings. Each child's `path` is its full absolute
  // path — NOT relative to the parent — because that's how consumers configure
  // them (see explanation above).
  if (route.children && route.children.length > 0) {
    for (const child of route.children) {
      for (const el of buildRouteElements(child, seen)) {
        elements.push(el);
      }
    }
  }

  return elements;
}

export interface ShellRouterProps {
  /** Route modules to render */
  routes: RouteModule[];
  /** Default redirect path for index route */
  defaultRoute?: string;
  /** Custom 404 component (defaults to redirect to defaultRoute) */
  notFoundElement?: ReactNode;
}

/** Generate Route elements from shell config */
export function buildShellRoutes({ routes, defaultRoute, notFoundElement }: ShellRouterProps): React.JSX.Element[] {
  const elements: React.JSX.Element[] = [];
  const seen = new Set<string>();

  // Index redirect
  if (defaultRoute) {
    elements.push(<Route key="__index" index element={<Navigate to={defaultRoute} replace />} />);
  }

  // Route modules — emit parent + children as flat siblings, deduped by path
  for (const route of routes) {
    for (const el of buildRouteElements(route, seen)) {
      elements.push(el);
    }
  }

  // Catch-all 404
  if (notFoundElement) {
    elements.push(<Route key="__catch-all" path="*" element={notFoundElement} />);
  } else if (defaultRoute) {
    elements.push(<Route key="__catch-all" path="*" element={<Navigate to={defaultRoute} replace />} />);
  }

  return elements;
}
